import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PRODUCT_CATEGORIES } from "@/data/site";

const EMPTY = {
  name: "",
  company: "",
  website: "",
  email: "",
  phone: "",
  product_category: "",
  current_channels: "",
  partnership_goals: "",
  message: "",
};

export function LeadForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Please add your name, company, and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Thanks! Our partnerships team will review and reach out.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="lead-form-success"
        className="flex flex-col items-center justify-center border border-border bg-surface p-12 text-center"
      >
        <CheckCircle2 className="text-orange" size={48} />
        <h3 className="mt-4 font-heading text-2xl font-bold text-navy">Partnership request received</h3>
        <p className="mt-2 max-w-sm text-sm text-foreground/70">
          A Hays Holdings partnerships specialist will review your brand and reach out within one business day.
        </p>
        <button
          data-testid="lead-form-reset"
          onClick={() => {
            setForm(EMPTY);
            setDone(false);
          }}
          className="mt-6 text-sm font-semibold text-brand hover:underline"
        >
          Submit another request
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      <div className="flex flex-col gap-2">
        <Label htmlFor="name">Name *</Label>
        <Input id="name" data-testid="lead-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Carter" />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="company">Company *</Label>
        <Input id="company" data-testid="lead-company" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Your brand / manufacturer" />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="website">Website</Label>
        <Input id="website" data-testid="lead-website" value={form.website} onChange={(e) => update("website", e.target.value)} placeholder="https://" />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="email">Email *</Label>
        <Input id="email" type="email" data-testid="lead-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="phone">Phone</Label>
        <Input id="phone" data-testid="lead-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 123-4567" />
      </div>
      <div className="flex flex-col gap-2">
        <Label>Product Category</Label>
        <Select value={form.product_category} onValueChange={(v) => update("product_category", v)}>
          <SelectTrigger data-testid="lead-category"><SelectValue placeholder="Select a category" /></SelectTrigger>
          <SelectContent>
            {PRODUCT_CATEGORIES.map((o) => (
              <SelectItem key={o} value={o} data-testid={`lead-category-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-2 sm:col-span-2">
        <Label htmlFor="channels">Current Sales Channels</Label>
        <Input id="channels" data-testid="lead-channels" value={form.current_channels} onChange={(e) => update("current_channels", e.target.value)} placeholder="e.g. Amazon, our website, retail, distributors" />
      </div>
      <div className="flex flex-col gap-2 sm:col-span-2">
        <Label htmlFor="goals">Partnership Goals</Label>
        <Textarea id="goals" data-testid="lead-goals" rows={3} value={form.partnership_goals} onChange={(e) => update("partnership_goals", e.target.value)} placeholder="What are you hoping to achieve? (e.g. grow Amazon sales, expand distribution, enter new channels)" />
      </div>
      <div className="flex flex-col gap-2 sm:col-span-2">
        <Label htmlFor="message">Message</Label>
        <Textarea id="message" data-testid="lead-message" rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your brand and products..." />
      </div>
      <button
        type="submit"
        data-testid="lead-submit"
        disabled={submitting}
        className="flex items-center justify-center gap-2 bg-orange px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-orange-dark disabled:opacity-60 sm:col-span-2"
      >
        {submitting && <Loader2 className="animate-spin" size={16} />}
        {submitting ? "Submitting..." : "Request Partnership Review"}
      </button>
    </form>
  );
}
